import React, { useReducer, useContext, createContext } from "react";
import API from "../../network/redAPI";
import { checkAuthError, logoutuser } from "../../helper/constant";

const INITIAL_STATE = {
	items: [],
	totalItemsCount: 0,
	favoritesMessage: null,
	isAddLoading: [],
	isRemoveLoading: [],
	isLoading: false,
	next: 1,
	isLoadMore: true,
};

const TYPES = {
	fetchFavorites: "FETCH_FAVORITES",
	loadMoreFavorites: "LOAD_MORE_FAVORITES",
	isNext: "IS_NEXT",
	notLoadMore: "NOT_LOAD_MORE",
	resetLoadMoreFavorites: "RESET_LOAD_MORE_FAVORITES",
	isLoading: "IS_LOADING",
	notLoading: "NOT_LOADING",
	isAddLoading: "IS_ADD_LOADING",
	notAddLoading: "NOT_ADD_LOADING",
	isRemoveLoading: "IS_REMOVING_LOADING",
	notRemoveLoading: "NOT_REMOVING_LOADING",
};

const reducer = (state, action) => {
	switch (action.type) {
		case TYPES.fetchFavorites:
			return {
				...state,
				items: action.payload,
				totalItemsCount: action.totalItems,
				favoritesMessage: action.favoritesMessage
			};
		case TYPES.isNext:
			return { ...state, next: action.payload };
		case TYPES.notLoadMore:
			return { ...state, isLoadMore: false };
		case TYPES.isLoading:
			return { ...state, isLoading: true };
		case TYPES.notLoading:
			return { ...state, isLoading: false };
		case TYPES.loadMoreFavorites:
			return { ...state, items: action.payload };
		case TYPES.resetLoadMoreFavorites:
			return { ...state, isLoadMore: true, next: 1 };
		case TYPES.isAddLoading: {
			let loading = state.isAddLoading.slice();
			loading[action.payload] = true;
			return { ...state, isAddLoading: loading };
		}
		case TYPES.notAddLoading: {
			let loading = state.isAddLoading.slice();
			loading[action.payload] = false;
			return { ...state, isAddLoading: loading };
		}
		case TYPES.isRemoveLoading: {
			let loading = state.isRemoveLoading.slice();
			loading[action.payload] = true;
			return { ...state, isRemoveLoading: loading };
		}
		case TYPES.notRemoveLoading: {
			let loading = state.isRemoveLoading.slice();
			loading[action.payload] = false;
			return { ...state, isRemoveLoading: loading };
		}
		case "":
			return { ...state };
		default:
			throw new Error(`Unknown action: ${action.type}`);
	}
};

export const FavoritesContext = createContext({});

const useFavoritesAction = (initialWishlist = INITIAL_STATE) => {
	const [state, dispatch] = useReducer(reducer, initialWishlist);

	const SetIsLoading = () => {
		dispatch({
			type: TYPES.isLoading,
		});
	};

	const SetIsNotLoading = () => {
		dispatch({
			type: TYPES.notLoading,
		});
	};

	const FetchFavoritesController = async (query) => {
		SetIsLoading();
		await API.FetchFavoritesItems(query)
			.then((response) => {
				if (checkAuthError(response)) {
					//logout
					throw new Error("Unauthorized");
				} else {
					return response.json();
				}
			})
			.then((data) => {
				if (data.success) {
					dispatch({
						type: TYPES.fetchFavorites,
						payload: data.data.list,
						totalItems: data.data.total_items,
						favoritesMessage: data.data?.message ?? null,
					});
				} else {
					window.alert(data.message);
				}
				SetIsNotLoading();
			})
			.catch((err) => {
				if (err.message === "Unauthorized") {
					alert("Your login session is invalid, please login again");
					logoutuser();
				}
			});
	};

	const LoadMoreFavoritesController = async (query) => {
		const nextNext = state.next + 1;
		let currentFavorites = [...state.items];

		if (state.isLoadMore) {
			await API.FetchFavoritesItems({ ...query, page: nextNext })
				.then((response) => {
					if (checkAuthError(response)) {
						//logout
						throw new Error("Unauthorized");
					} else {
						return response.json();
					}
				})
				.then((data) => {
					if (data.success) {
						if (data.data.next !== null && data.data.list.length) {
							const result = [];
							const map = new Map();
							let allData = currentFavorites.concat(data.data.list);

							for (const item of allData) {
								if (!map.has(item.item_id)) {
									map.set(item.item_id, true);
									result.push(item);
								}
							}

							dispatch({
								type: TYPES.loadMoreFavorites,
								payload: result,
							});
							dispatch({
								type: TYPES.isNext,
								payload: nextNext,
							});
						} else {
							dispatch({
								type: TYPES.notLoadMore,
							});
						}
					}
				})
				.catch((err) => {
					if (err.message === "Unauthorized") {
						alert("Your login session is invalid, please login again");
						logoutuser();
					}
				});
		}
	};

	const AddToFavoritesController = async (body) => {
		// SetIsLoading();
		dispatch({
			type: TYPES.isAddLoading,
			payload: body.item_id,
		});

		await API.AddItemToFavorites(body)
			.then((response) => {
				if (checkAuthError(response)) {
					//logout
					throw new Error("Unauthorized");
				} else {
					return response.json();
				}
			})
			.then(async (data) => {
				if (!data.success) {
					alert(data.message);
				}
				await FetchFavoritesController({ type: body.type });
				// SetIsNotLoading();
				dispatch({
					type: TYPES.notAddLoading,
					payload: body.item_id,
				});
			})
			.catch((err) => {
				if (err.message === "Unauthorized") {
					alert("Your login session is invalid, please login again");
					logoutuser();
				}
			});
	};

	const RemoveFromFavoritesController = async (query, notificationId) => {
		dispatch({
			type: TYPES.isRemoveLoading,
			payload: query.item_id,
		});
		await API.RemoveItemFromFavorites(query)
			.then((response) => {
				if (checkAuthError(response)) {
					//logout
					throw new Error("Unauthorized");
				} else {
					return response.json();
				}
			})
			.then(async (data) => {
				if (!data.success) {
					alert(data.message);
				}
				await FetchFavoritesController({ type: query.type, notificationId: notificationId !== "undefined" ? notificationId : null });

				dispatch({
					type: TYPES.notRemoveLoading,
					payload: query.item_id,
				});
			})
			.catch((err) => {
				if (err.message === "Unauthorized") {
					alert("Your login session is invalid, please login again");
					logoutuser();
				}
			});
	};

	const RemoveAllFromFavoritesController = async (query) => {
		SetIsLoading();
		await API.RemoveAllItemFromFavorites(query)
			.then((response) => {
				if (checkAuthError(response)) {
					//logout
					throw new Error("Unauthorized");
				} else {
					return response.json();
				}
			})
			.then(async (data) => {
				if (data.success) {
					await FetchFavoritesController({ type: query.type });
				} else {
					alert(data.message);
				}
				SetIsNotLoading();
			})
			.catch((err) => {
				if (err.message === "Unauthorized") {
					alert("Your login session is invalid, please login again");
					logoutuser();
				}
			});
	};

	const ResetLoadMoreFavoriteController = () => {
		dispatch({
			type: TYPES.resetLoadMoreFavorites,
		});
	};

	const IsInFavoritesController = (itemId) => {
		return state.items?.some(
			(item) => parseInt(item.item_id) === parseInt(itemId)
		);
	};

	const GetItemHandler = (itemId) => {
		return state.items?.find(
			(item) => parseInt(item.item_id) === parseInt(itemId)
		);
	};

	return {
		state,
		FetchFavoritesController,
		AddToFavoritesController,
		RemoveFromFavoritesController,
		RemoveAllFromFavoritesController,
		LoadMoreFavoritesController,
		IsInFavoritesController,
		GetItemHandler,
		ResetLoadMoreFavoriteController,
	};
};

export const FavoritesProvider = ({ children }) => {
	const {
		state,
		FetchFavoritesController,
		AddToFavoritesController,
		RemoveFromFavoritesController,
		RemoveAllFromFavoritesController,
		LoadMoreFavoritesController,
		IsInFavoritesController,
		GetItemHandler,
		ResetLoadMoreFavoriteController,
	} = useFavoritesAction();

	return (
		<FavoritesContext.Provider
			value={{
				state: state,
				next: state.next,
				isLoadMore: state.isLoadMore,
				favLoading: state.isLoading,
				favAddLoading: state.isAddLoading,
				favRemoveLoading: state.isRemoveLoading,
				favorites: state.items,
				favoritesMessage: state.favoritesMessage,
				totalFavoriteItems: state.totalItemsCount,
				resetLoadMoreFavorites: ResetLoadMoreFavoriteController,
				fetchFavorites: FetchFavoritesController,
				addItemToFavorites: AddToFavoritesController,
				removeItemFromFavorites: RemoveFromFavoritesController,
				removeAllItemsFromFavorites: RemoveAllFromFavoritesController,
				handleLoadMore: LoadMoreFavoritesController,
				isInFavorites: IsInFavoritesController,
				getItem: GetItemHandler,
			}}
		>
			{children}
		</FavoritesContext.Provider>
	);
};

export const useFavorites = () => useContext(FavoritesContext);
